import { createPortal } from "react-dom";
import type { CardDef, GameView } from "../types";
import { eventDescriptions, eventLabels } from "../gameMeta";
import { GameCard } from "./Card";
import { EventFaceIcon } from "./EventFaceIcon";
import { LastRollEvent, lastRollEventTitle } from "./LastRollEvent";

interface Props {
  game: GameView;
  definitions: Map<string, CardDef>;
  showRollResult?: boolean;
  onContinue?: () => void;
}

export function rollPresentationKey(game: GameView): string | null {
  const roll = game.turn.last_roll;
  if (!roll) return null;
  return [
    roll.turn_number ?? game.turn.number,
    roll.roller ?? game.turn.active,
    roll.production,
    roll.event,
    roll.event_card_id || "geen-kaart",
    game.recovery_note?.at || "origineel",
  ].join(":");
}

export function Supply({ game, definitions, showRollResult = false, onContinue }: Props) {
  const roll = game.turn.last_roll;
  const eventDefinition = roll?.event_card_id ? definitions.get(roll.event_card_id) : undefined;
  const eventDescription = roll
    ? roll.event === "gebeurtenis" && eventDefinition
      ? eventDefinition.text
      : eventDescriptions[roll.event]
    : "";
  const presentationKey = rollPresentationKey(game);
  const rollerName = roll?.roller
    ? game.players.find((player) => player.seat === roll.roller)?.name
    : undefined;
  const lastEventTitle = roll ? lastRollEventTitle(roll, eventDefinition) : "";

  return (
    <section className="central-supply" aria-label="Centrale voorraad">
      {showRollResult && roll && presentationKey ? createPortal((
        <div className="roll-animation" role="dialog" aria-modal="true" aria-labelledby="roll-result-title" aria-describedby="roll-result-description">
          <section className={`roll-stage ${roll.event === "gebeurtenis" ? "with-event-card" : ""}`}>
            <div className="roll-main">
              <span className="eyebrow">Nieuwe worp</span>
              <div className="rolling-dice" aria-label={`Opbrengstworp ${roll.production}, ${eventLabels[roll.event]}`}>
                <span className="rolling-die production-die">{roll.production}</span>
                <span className="rolling-die event-die"><EventFaceIcon event={roll.event} /></span>
              </div>
              <div className="roll-reveal">
                <small>Opbrengstworp</small>
                <strong>{roll.production}</strong>
                <b id="roll-result-title">{eventLabels[roll.event]}</b>
                <p id="roll-result-description">{eventDescription}</p>
              </div>
              <button type="button" className="primary roll-continue" autoFocus onClick={onContinue}>Doorgaan</button>
            </div>
            {roll.event === "gebeurtenis" ? (
              <aside className="event-card-reveal" aria-label="Getrokken gebeurteniskaart">
                {eventDefinition && roll.event_card_id ? (
                  <GameCard
                    instance={{ instance_id: 0, card_id: roll.event_card_id }}
                    definition={eventDefinition}
                  />
                ) : null}
                <div className="event-card-summary">
                  <span className="eyebrow">Getrokken gebeurteniskaart</span>
                  <h3>{eventDefinition?.name || "Gebeurteniskaart"}</h3>
                  <p>{eventDefinition?.text || "De gebeurteniskaart wordt uitgevoerd voordat het spel verdergaat."}</p>
                </div>
              </aside>
            ) : null}
          </section>
        </div>
      ), document.body, `roll:${presentationKey}`) : null}
      <div className="dice-result">
        {roll ? (
          <>
            <span className="die production-die">{roll.production}</span>
            <LastRollEvent roll={roll} definition={eventDefinition} rollerName={rollerName} />
            <div><strong>{lastEventTitle}</strong><small>Laatste worp · opbrengst {roll.production} · details bij symbool</small></div>
          </>
        ) : (
          <><span className="die muted">?</span><div><strong>Nog niet geworpen</strong><small>Deze beurt</small></div></>
        )}
      </div>
      <div className="supply-piles">
        <div className="piece-supply"><span>═</span><strong>{game.supply.roads}</strong><small>straten</small></div>
        <div className="piece-supply"><span>⌂</span><strong>{game.supply.villages}</strong><small>dorpen</small></div>
        <div className="piece-supply"><span>♜</span><strong>{game.supply.cities}</strong><small>steden</small></div>
        <div className="piece-supply"><span>◇</span><strong>{game.supply.region_cards}</strong><small>landschappen</small></div>
      </div>
      <div className="deck-shelf">
        {game.supply.decks.map((deck) => (
          <div className={`mini-deck set-${deck.set}`} key={deck.deck_id} title={deck.label}>
            <i /><i /><span>{deck.deck_id}</span><strong>{deck.count}</strong>
          </div>
        ))}
        <div className="event-stack" title="Gebeurtenissen"><EventFaceIcon event="gebeurtenis" /><strong>{game.supply.event_cards}</strong></div>
        <div className="discard-stack" title="Aflegstapel">
          {game.supply.discard_top ? <GameCard instance={game.supply.discard_top} definition={definitions.get(game.supply.discard_top.card_id)} compact /> : <span>leeg</span>}
          <b>{game.supply.discard_count}</b>
        </div>
      </div>
    </section>
  );
}
